/**
 * Global Error Boundary.
 *
 * Catches unhandled errors during React rendering and displays a
 * recovery UI instead of a blank white screen. Shows the error
 * message in development to aid debugging, and offers a retry button
 * that resets the boundary state so the user can continue working.
 */

import { Component } from "react";
import type { ErrorInfo, ReactNode } from "react";
import { AlertTriangle, RotateCcw } from "lucide-react";

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error("[ErrorBoundary]", error, info.componentStack);
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (!this.state.hasError) {
      return this.props.children;
    }

    return (
      <div className="min-h-screen flex items-center justify-center bg-[var(--surface-sunken)] p-6">
        <div className="max-w-md w-full rounded-[var(--radius-lg)] border border-[var(--border-default)] bg-[var(--surface-base)] shadow-[var(--shadow-lg)] p-8 text-center">
          <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-amber-100 dark:bg-amber-900/50">
            <AlertTriangle className="h-6 w-6 text-amber-600 dark:text-amber-300" />
          </div>
          <h2 className="text-lg font-bold text-[var(--text-primary)] mb-2">
            Something went wrong
          </h2>
          <p className="text-sm text-[var(--text-secondary)] mb-4">
            An unexpected error occurred. You can try again or refresh the page.
          </p>
          {import.meta.env.DEV && this.state.error && (
            <pre className="mb-4 max-h-40 overflow-auto rounded-[var(--radius-md)] bg-[var(--surface-sunken)] border border-[var(--border-subtle)] p-3 text-left text-xs text-red-600 dark:text-red-300 whitespace-pre-wrap break-words">
              {this.state.error.message}
              {this.state.error.stack && (
                <>
                  {"\n\n"}
                  {this.state.error.stack}
                </>
              )}
            </pre>
          )}
          <div className="flex items-center justify-center gap-3">
            <button
              onClick={this.handleReset}
              className="inline-flex items-center gap-2 rounded-[var(--radius-md)] bg-[var(--color-interactive-primary)] px-4 py-2 text-sm font-medium text-white hover:opacity-90 transition-opacity"
            >
              <RotateCcw className="h-4 w-4" />
              Try Again
            </button>
            <button
              onClick={() => window.location.reload()}
              className="inline-flex items-center gap-2 rounded-[var(--radius-md)] border border-[var(--border-default)] bg-[var(--surface-base)] px-4 py-2 text-sm font-medium text-[var(--text-primary)] hover:bg-[var(--color-surface-hover)] transition-colors"
            >
              Refresh Page
            </button>
          </div>
        </div>
      </div>
    );
  }
}

export default ErrorBoundary;
